import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  CloudFog,
  Crosshair,
  Drone,
  Droplets,
  Globe2,
  Grid3x3,
  Layers,
  Milestone,
  MountainSnow,
  Network,
  PencilRuler,
  Radar,
  RotateCcw,
  Route,
  ScanEye,
  ShieldAlert,
  Spline,
  Sun,
  Shapes,
  SplitSquareVertical,
  SquareDashed,
  Layers2,
  Swords,
  Tags,
  TreePine,
  UserRound,
  Users,
  Waves,
} from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import { isManeuverUnit } from "./units/maneuverUnits";

import { api } from "../api/client";
import type { DeployedUnit, LayerKey, Phase } from "../api/types";
import { attentionSelector, designateSoon } from "../components/attention";
import { Checkbox } from "../components/Checkbox";
import { Disclosure, DisclosureChevron, PanelGroup, TreeRow } from "../components/panel";
import { Motion, Presence } from "../components/presence";
import { Button, FloatingPanel, IconButton, Segmented } from "../components/ui";
import { HelpButton } from "../help/HelpButton";
import { config } from "../config";
import { effectiveBasemap as basemapShown, forcedBasemap } from "./zones/basemap";
import { useOpenZone } from "./zones/useZones";
import { echelonName, echelonNameFromSidc } from "../echelon";
import { unitDesignation } from "../i18n/designation";
import { k, lang, locale, t } from "../i18n/i18n";
import { term } from "../i18n/vocabulary";
import { annotationControl, type Basemap, type DroneControlKey, type ObjectsFilter, useUi } from "../state/ui";
import { SymbolThumb } from "./annotations/AnnotationMenu";
import { CATEGORIES, labelled, sidcOf, symbolOf, symbolsOf } from "./annotations/catalog";
import { annotationsOf } from "./dossierWrites";
import { graphicsCount, graphicsOf } from "./graphics/useGraphics";
import { PENETRANTE_RED, ROCADE_GREEN, useTacticalRoads } from "./roads/TacticalRoadsLayers";
import { useLineLabel } from "./lineNames";
import { namesShown, setNamesShown } from "./manoeuvre/MeasureNamesToggle";
import { measureLevelOf } from "./manoeuvre/measureView";
import { linesAt } from "./manoeuvre/zoMeasures";
import { useMapRevealed } from "./reveal";
import { symbolSvg } from "./units/symbols";
import { isCompany } from "../company";
import { useDeployment } from "./units/useDeployment";
import { useUnitMoves } from "./units/useUnitMoves";
import { useCountryBorders } from "./country/CountryBordersLayers";
import { fireZoneCount, useFireZones } from "./fires/FireZonesLayers";
import { countryCount } from "./country/countryBorders";
import { COUNTRY_WRITE_TARGET } from "../views/country/countrySheet";
import { openMissionData } from "../views/openMissionData";
import { WEATHER_FIELD } from "../views/missionDataTarget";
import { visibilityText } from "../weather";
import { anyInactive, type DroneRow, droneRows, ENEMIES_ROW_TARGET, TERRAIN_ANALYZE_TARGET, terrainDronesOf } from "./drones/dronesFeatures";
import { KIND_COLOUR, ROZ_ORANGE, useDroneAirspace } from "./drones/DronesLayers";
import { LINK_LOSS_AMBER } from "./terrain/TerrainLayers";
import { FRIEND_BLUE } from "./units/symbols";
import { ENEMY_RING_RED, useVisibility, VEIL_GREY } from "./visibility/VisibilityLayers";

interface Item {
  key: LayerKey;
  label: string;
  icon: ReactNode;
}

/** Groupes des couches du camp ; `borders` : le groupe porte aussi les frontières des pays fictifs de la fiche
 *  pays (contexte géographique, comme la limite du camp). */
const GROUPS: { label: string; items: Item[]; borders?: boolean }[] = [
  {
    label: k("Éléments naturels"),
    items: [
      { key: "forest", label: k("Forêts"), icon: <TreePine size={14} /> },
      { key: "water", label: k("Plans d'eau"), icon: <Droplets size={14} /> },
      { key: "rivers", label: k("Cours d'eau"), icon: <Waves size={14} /> },
    ],
  },
  {
    label: k("Infrastructures"),
    items: [
      { key: "urban", label: k("Zones urbaines"), icon: <Building2 size={14} /> },
      { key: "roads", label: k("Routes"), icon: <Route size={14} /> },
      { key: "bridges", label: k("Ponts"), icon: <Spline size={14} /> },
      { key: "chokepoints", label: k("Points de passage obligés"), icon: <ShieldAlert size={14} /> },
    ],
  },
  {
    label: k("Référentiel"),
    items: [
      { key: "boundary", label: k("Limite du camp"), icon: <SquareDashed size={14} /> },
      { key: "grid", label: k("Grille de placement"), icon: <Grid3x3 size={14} /> },
    ],
    borders: true,
  },
];

const BASEMAPS: { key: Basemap; label: string; available: boolean }[] = [
  { key: "mapbox", label: k("Carte Mapbox"), available: config.mapEngine === "mapbox" },
  { key: "ign-plan", label: k("Plan IGN"), available: true },
  { key: "ign-ortho", label: k("Ortho IGN"), available: true },
];

/** Libellé d'une ligne de calque : case à cocher (toute la ligne la bascule), icône de 14 px, nom. `blocked` +
 *  `onBlocked` : préalable manquant, le clic désigne l'action attendue (R12). */
function LayerToggle({
  label,
  icon,
  checked,
  onChange,
  disabled,
  blocked,
  onBlocked,
}: {
  label: string;
  icon: ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  blocked?: boolean;
  onBlocked?: () => void;
}) {
  return (
    <Checkbox
      checked={checked}
      disabled={disabled}
      blocked={blocked}
      onBlocked={onBlocked}
      onChange={onChange}
      // Anneau de focus vers l'intérieur : la ligne d'arbre rogne son libellé (points de suspension).
      className="focus-inset w-full"
      label={
        <span className="flex min-w-0 items-center gap-2">
          <span className="flex shrink-0 text-ink-muted">{icon}</span>
          <span className="min-w-0 truncate">{label}</span>
        </span>
      }
    />
  );
}

/** Ligne de calque (ligne d'arbre de 24 px, R4) : case, icône, nom, compteur en `Badge` à droite ; `depth` 2 pour
 *  un réglage rattaché à la ligne du dessus. */
function LayerRow({
  label,
  icon,
  count,
  checked,
  onChange,
  disabled,
  title,
  depth,
  trailing,
}: {
  label: string;
  icon: ReactNode;
  count?: number;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  title?: string;
  depth?: number;
  trailing?: ReactNode;
}) {
  return (
    <TreeRow
      depth={depth}
      title={title}
      label={<LayerToggle label={label} icon={icon} checked={checked} onChange={onChange} disabled={disabled} />}
      count={count}
      trailing={trailing}
    />
  );
}

/** Ouvre une liste de la carte (ordre de bataille, palettes des graphiques et des annotations) : depuis le
 *  28/10/2026, les « Objets de la mission » ne sont plus dans la barre latérale (demande d'Alexis). */
function OpenObjects({
  filter,
  label,
  icon,
  size = "sm",
}: {
  filter: ObjectsFilter;
  label: string;
  icon: ReactNode;
  size?: "xs" | "sm";
}) {
  const showObjects = useUi((s) => s.showObjects);
  const exerciseId = useUi((s) => s.exerciseId);
  return (
    <IconButton size={size} title={label} aria-label={label} disabled={!exerciseId} onClick={() => showObjects(filter)}>
      {icon}
    </IconButton>
  );
}

/** Frontières des pays fictifs de la fiche pays (demande d'Alexis du 11/10/2026) : affichées par défaut dès que la
 *  fiche existe ; sans fiche, l'entrée paraît inactive et son clic ouvre la vue « Fiche pays » en y désignant
 *  « Rédiger la fiche pays » (R12). */
function CountryBordersRow() {
  const { state, borders } = useCountryBorders();
  const checked = useUi((s) => s.controlVisible.countryBorders !== false);
  const setControlVisible = useUi((s) => s.setControlVisible);
  const setView = useUi((s) => s.setView);
  const notify = useUi((s) => s.notify);
  const count = countryCount(borders);
  return (
    <TreeRow
      title={
        state === "none"
          ? t("Pas encore de fiche pays : rédigez-la pour afficher les frontières de ses pays")
          : t("Frontières des pays fictifs de la fiche pays : le pays de la fiche en rouge ennemi")
      }
      label={
        <LayerToggle
          label={t("Frontières des pays fictifs")}
          icon={<Globe2 size={14} />}
          checked={state === "ready" && checked}
          onChange={(v) => setControlVisible("countryBorders", v)}
          disabled={state === "off" || state === "loading"}
          blocked={state === "none"}
          onBlocked={() => {
            setView("country");
            designateSoon(attentionSelector(COUNTRY_WRITE_TARGET));
            notify({ text: t("Rédigez d'abord la fiche pays"), tone: "info" });
          }}
        />
      }
      count={state === "ready" && count > 0 ? count : undefined}
    />
  );
}

/** Un camp (amis, ennemis, populations) de l'ordre de bataille : case d'affichage, puis liste dépliable des
 *  unités comme dans Prevail (symbole et désignation ; un clic sélectionne l'unité et centre la carte dessus). */
function SideRow({
  label,
  icon,
  units,
  unplaced = [],
  phase,
  checked,
  onChange,
  disabled,
  attention,
}: {
  label: string;
  icon: ReactNode;
  units: DeployedUnit[];
  unplaced?: string[];
  phase: Phase;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  /** Cible d'une désignation (`data-attention`), quand une autre ligne attend celle-ci. */
  attention?: string;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const select = useUi((s) => s.select);
  const selection = useUi((s) => s.selection);
  const flyTo = useUi((s) => s.requestFlyTo);
  const shown = units.filter((u) => u.positions[phase] && !u.hidden);
  const expandable = !disabled && units.length + unplaced.length > 0;
  return (
    <div data-attention={attention} className="rounded-md">
      <TreeRow
        label={<LayerToggle label={label} icon={icon} checked={checked} onChange={onChange} disabled={disabled} />}
        count={shown.length}
        expanded={open}
        onToggle={expandable ? () => setOpen(!open) : undefined}
        toggleLabel={open ? t("Replier l'ordre de bataille") : t("Déplier l'ordre de bataille")}
      />
      <Disclosure open={open && expandable} id={id}>
        <ul className="space-y-px pb-1">
          {units.map((u) => {
            const position = u.positions[phase];
            const onMap = Boolean(position) && !u.hidden;
            const selected = selection?.layer === "unit" && selection.code === u.code;
            // Échelon : code du serveur en minuscules (« compagnie ») ; en anglais, son nom OTAN (« company »).
            // Unité construite : l'échelon APP-6 de son symbole, nommé dans la langue de l'interface.
            const echelon = u.app6
              ? echelonNameFromSidc(u.sidc, u.arm, u.side === "AMI")
              : lang() === "en"
                ? echelonName(u.echelon, u.arm, u.side === "AMI")
                : u.echelon;
            const details = [unitDesignation(u.display_name), term(u.specialty), echelon?.toLowerCase()].filter(Boolean).join(" · ");
            return (
              <li key={u.code}>
                <TreeRow
                  depth={2}
                  title={onMap ? details || unitDesignation(u.designation) : u.hidden ? t("Sans mission : non dessinée sur la carte") : t("Absente de la carte en {phase}", { phase })}
                  icon={
                    <span
                      className={
                        onMap
                          ? "flex h-4 w-6 shrink-0 items-center justify-center [&>svg]:max-h-4 [&>svg]:max-w-6"
                          : "flex h-4 w-6 shrink-0 items-center justify-center opacity-40 [&>svg]:max-h-4 [&>svg]:max-w-6"
                      }
                      dangerouslySetInnerHTML={{ __html: symbolSvg(u.sidc, 13) }}
                    />
                  }
                  label={onMap ? unitDesignation(u.designation) : <span className="text-ink-muted">{unitDesignation(u.designation)}</span>}
                  selected={selected}
                  onClick={
                    onMap && position
                      ? () => {
                          select({ layer: "unit", code: u.code, lngLat: [position.lon, position.lat] });
                          flyTo([position.lon, position.lat], 13);
                        }
                      : undefined
                  }
                />
              </li>
            );
          })}
          {unplaced.length > 0 && (
            <li className="pt-1 pr-2 text-meta text-ink-muted" style={{ paddingLeft: 4 + 2 * 14 + 22 }}>
              <span className="font-medium">{t("Non placées ({n}) :", { n: unplaced.length })} </span>
              {unplaced.map(unitDesignation).join(", ")}
            </li>
          )}
        </ul>
      </Disclosure>
    </div>
  );
}

/** Groupe de calques repliable : titre gris (`PanelGroup`), chevron qui pivote, compteur, contenu déplié en
 *  douceur (`Disclosure`). */
function LayerGroup({
  label,
  total,
  actions,
  children,
}: {
  label: string;
  total?: number;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(true);
  const id = useId();
  return (
    <PanelGroup
      title={
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-controls={id}
          className="focus-inset flex w-full items-center gap-1.5 rounded-md text-left transition-colors hover:text-ink"
        >
          <DisclosureChevron open={open} />
          {label}
        </button>
      }
      count={total}
      actions={actions}
    >
      <Disclosure open={open} id={id}>
        {children}
      </Disclosure>
    </PanelGroup>
  );
}

/** Libellé, icône et info-bulle de chaque ligne du groupe « Drones ». */
const DRONE_ROW_META: Record<DroneControlKey, { label: string; icon: ReactNode; title: string }> = {
  droneVaa: {
    label: k("Volumes aériens alloués (VAA)"),
    icon: <Drone size={14} />,
    title: k("Volume où nos drones volent sans demande, du sol à son plafond, désactivé au passage de l'ALAT"),
  },
  droneRoz: {
    label: k("ROZ drones"),
    icon: <Radar size={14} />,
    title: k("Zone d'opérations restreinte des drones tactiques, au-dessus du volume alloué"),
  },
  droneNai: {
    label: k("Zones d'intérêt du renseignement (NAI)"),
    icon: <ScanEye size={14} />,
    title: k("Zones où nos drones recherchent les indices des modes d'action ennemis"),
  },
  droneTerrain: {
    label: k("Éléments drones de l'analyse du terrain"),
    icon: <MountainSnow size={14} />,
    title: k("Zones de mise en œuvre de nos drones et terrain masqué à leur liaison par le relief"),
  },
};

/** Trait de légende : plein, tireté ou tireté court, à la couleur du calque. */
function LegendStroke({ colour, dash }: { colour: string; dash?: "long" | "short" }) {
  return (
    <span
      aria-hidden
      className="w-4 shrink-0"
      style={{ borderTop: `2px ${dash ? "dashed" : "solid"} ${colour}`, ...(dash === "short" ? { borderTopStyle: "dotted" } : {}) }}
    />
  );
}

/** Ligne de légende : repère, puis texte gris. */
function LegendLine({ mark, children }: { mark: ReactNode; children: ReactNode }) {
  return (
    <span className="flex items-center gap-1.5">
      {mark}
      <span className="min-w-0">{children}</span>
    </span>
  );
}

/** Groupe « Drones » du panneau Calques (D13) : VAA, ROZ drones, NAI et éléments drones de l'analyse du terrain,
 *  décochés par défaut ; ligne bloquée → action attendue désignée (NAI sans ennemi placé : la ligne « Ennemis » ;
 *  éléments de l'analyse sans analyse : « Analyser le terrain ») ; légende des lignes cochées en fondu. */
function DronesGroup({ level, centac, enemies }: { level: string | null; centac: boolean; enemies: number }) {
  const controlVisible = useUi((s) => s.controlVisible);
  const setControlVisible = useUi((s) => s.setControlVisible);
  const exerciseId = useUi((s) => s.exerciseId);
  const phase = useUi((s) => s.phase);
  const report = useUi((s) => s.terrain);
  const setView = useUi((s) => s.setView);
  const notify = useUi((s) => s.notify);
  const revealed = useMapRevealed();
  const { data: airspace } = useDroneAirspace();
  const rows = droneRows({ level, centac, saved: Boolean(exerciseId) && revealed, airspace, enemies, report }).filter((r) => r.present);
  if (!rows.length) return null;
  const on = (key: DroneControlKey) => controlVisible[key] === true;
  const shown = { vaa: on("droneVaa"), roz: on("droneRoz"), nai: on("droneNai") };
  const first = (kind: "vaa" | "roz") => airspace?.features.find((f) => f.properties.kind === kind && f.properties.role === "area")?.properties;
  const vaa = first("vaa");
  const roz = first("roz");
  const drones = terrainDronesOf(report);
  const onBlocked = (row: DroneRow) => {
    if (row.blocked === "enemy") {
      designateSoon(attentionSelector(ENEMIES_ROW_TARGET), "ring", 200);
      notify({ text: t("Placez d'abord l'ennemi : les NAI suivent ses modes d'action."), tone: "info" });
    } else if (row.blocked === "terrain") {
      setView("terrain");
      designateSoon(attentionSelector(TERRAIN_ANALYZE_TARGET));
      notify({ text: t("Analysez d'abord le terrain : ses éléments drones s'affichent ensuite."), tone: "info" });
    }
  };
  const legend = rows.some((r) => on(r.key) && !r.blocked && !r.disabled);
  return (
    <LayerGroup label={t("Drones")}>
      {rows.map((row) => {
        const meta = DRONE_ROW_META[row.key];
        return (
          <TreeRow
            key={row.key}
            title={t(meta.title)}
            label={
              <LayerToggle
                label={t(meta.label)}
                icon={meta.icon}
                checked={on(row.key) && !row.blocked}
                onChange={(v) => setControlVisible(row.key, v)}
                disabled={row.disabled}
                blocked={row.blocked !== null}
                onBlocked={() => onBlocked(row)}
              />
            }
            count={row.count}
          />
        );
      })}
      {/* Légende des lignes cochées (couleurs et traits de la carte), en fondu. */}
      <Presence show={legend}>
        <Motion origin="fade">
          <div className="flex flex-col gap-1 px-2 pt-1 pb-1.5 text-meta leading-snug text-ink-muted">
            {shown.vaa && vaa && (
              <LegendLine mark={<LegendStroke colour={KIND_COLOUR.vaa} />}>{`${vaa.label} · ${vaa.lines[0] ?? ""}`}</LegendLine>
            )}
            {shown.roz && roz && (
              <LegendLine mark={<LegendStroke colour={ROZ_ORANGE} dash="long" />}>{`${roz.label} · ${roz.lines[0] ?? ""}`}</LegendLine>
            )}
            {shown.nai && (
              <LegendLine mark={<LegendStroke colour={KIND_COLOUR.nai} dash="short" />}>
                {t("NAI : indices à rechercher, numérotées par priorité")}
              </LegendLine>
            )}
            {on("droneTerrain") && drones && drones.available && (
              <>
                <LegendLine mark={<Drone size={12} aria-hidden style={{ color: FRIEND_BLUE }} />}>{t("Zones de mise en œuvre")}</LegendLine>
                <LegendLine mark={<span aria-hidden className="size-2.5 shrink-0 rounded-sm" style={{ background: LINK_LOSS_AMBER, opacity: 0.6 }} />}>
                  {t("Terrain masqué à nos drones par le relief")}
                </LegendLine>
              </>
            )}
            {on("droneTerrain") && drones && !drones.available && drones.reason === "no_enemy" && (
              <span>{t("Placez l'ennemi pour situer les zones de mise en œuvre.")}</span>
            )}
            {anyInactive(airspace, shown, phase) && <span>{t("Estompé : hors du temps affiché ({phase}).", { phase })}</span>}
          </div>
        </Motion>
      </Presence>
    </LayerGroup>
  );
}

/** Ligne « Visibilité » du groupe « Météo » (conception validée par Alexis le 31/10/2026) : visibilité du jour à
 *  droite, sans compteur ; bloquée sans dossier ni unités (désigne le réglage Météo des Données initiales) ; légende
 *  en fondu : les anneaux par visibilité réduite, sinon la phrase de beau temps (rien n'est tracé). */
function VisibilityRow({ deployed }: { deployed: boolean }) {
  const checked = useUi((s) => s.controlVisible.visibility === true);
  const setControlVisible = useUi((s) => s.setControlVisible);
  const exerciseId = useUi((s) => s.exerciseId);
  const state = useVisibility();
  const blocked = !exerciseId || !deployed;
  const range = state?.range ? visibilityText(state.range) : null;
  return (
    <>
      <TreeRow
        title={t("Portées de vue et de tir direct réduites par la météo du dossier ; rien n'est tracé par bonne visibilité")}
        label={
          <LayerToggle
            label={t("Visibilité")}
            icon={<CloudFog size={14} />}
            checked={checked && !blocked}
            onChange={(v) => setControlVisible("visibility", v)}
            disabled={!blocked && state === null}
            blocked={blocked}
            onBlocked={() => openMissionData({ fallback: WEATHER_FIELD })}
          />
        }
        trailing={range && !blocked ? <span className="shrink-0 text-meta text-ink-muted tabular-nums">{range}</span> : undefined}
      />
      <Presence show={checked && !blocked && state !== null}>
        <Motion origin="fade">
          <div className="flex flex-col gap-1 px-2 pt-1 pb-1.5 text-meta leading-snug text-ink-muted">
            {state?.reduced ? (
              <>
                <LegendLine mark={<span aria-hidden className="size-3 shrink-0 rounded-full border-[1.5px]" style={{ borderColor: FRIEND_BLUE }} />}>
                  {t("Vue amie limitée à {km} km", { km: (state.friendlyKm ?? 0).toLocaleString(locale(), { maximumFractionDigits: 1 }) })}
                </LegendLine>
                <LegendLine mark={<span aria-hidden className="size-3 shrink-0 rounded-full border-[1.5px]" style={{ borderColor: ENEMY_RING_RED }} />}>
                  {t("Tir direct ennemi limité à {km} km", { km: (state.enemyKm ?? 0).toLocaleString(locale(), { maximumFractionDigits: 1 }) })}
                </LegendLine>
                <LegendLine mark={<span aria-hidden className="size-2.5 shrink-0 rounded-sm" style={{ background: VEIL_GREY, opacity: 0.5 }} />}>
                  {t("Visibilité réduite sur toute la zone")}
                </LegendLine>
              </>
            ) : (
              range && <span>{t("Bonne visibilité ({v}) : la vue n'est limitée que par le terrain.", { v: range })}</span>
            )}
          </div>
        </Motion>
      </Presence>
    </>
  );
}

export function LayersPanel() {
  const mapSlug = useUi((s) => s.mapSlug);
  const lineLabel = useLineLabel();
  const visible = useUi((s) => s.visible);
  const setVisible = useUi((s) => s.setVisible);
  const freeTerrainRivers = useUi((s) => s.freeTerrainRivers);
  const setFreeTerrainRivers = useUi((s) => s.setFreeTerrainRivers);
  const basemap = useUi((s) => s.basemap);
  const setBasemap = useUi((s) => s.setBasemap);
  const mapLighten = useUi((s) => s.mapLighten);
  const setMapLighten = useUi((s) => s.setMapLighten);
  const layersOpen = useUi((s) => s.layersOpen);
  const toggleLayers = useUi((s) => s.toggleLayers);
  const phase = useUi((s) => s.phase);
  const unitsVisible = useUi((s) => s.unitsVisible);
  const supportsVisible = useUi((s) => s.supportUnitsVisible);
  const setSupportsVisible = useUi((s) => s.setSupportUnitsVisible);
  const roleLabelsVisible = useUi((s) => s.roleLabelsVisible);
  const setRoleLabelsVisible = useUi((s) => s.setRoleLabelsVisible);
  const setUnitsVisible = useUi((s) => s.setUnitsVisible);
  const { data: preview } = useDeployment();
  // Carte vide tant que le dossier n'est pas créé : ni unités ni lignes à lister ou à compter.
  const revealed = useMapRevealed();
  const deployment = revealed ? preview : undefined;
  const supportCount = (deployment?.units ?? []).filter((u) => !u.hidden && !isManeuverUnit(u)).length;
  const corridor = deployment?.corridor ?? null;
  // Lignes et zones de la ZO (docs/15 §4.2) : absentes en CENTAC ; le compteur suit le temps affiché.
  const measures = measureLevelOf(deployment) ? (corridor?.measures ?? null) : null;
  const measureCount = measures ? linesAt(measures.lines, phase).length : 0;
  const controlVisible = useUi((s) => s.controlVisible);
  const annotations = annotationsOf(deployment);
  const setControlVisible = useUi((s) => s.setControlVisible);
  const exerciseId = useUi((s) => s.exerciseId);
  const { data: roads } = useTacticalRoads(controlVisible.roads === true && revealed);
  const roadCount = roads?.features.filter((f) => f.geometry.type === "LineString").length ?? 0;
  // ZEF et ZEA de l'annexe A : OPO de division seulement, lues même calque masqué (compteur de la ligne).
  const division = deployment?.level === "DIVISION";
  const { data: fireZones } = useFireZones(revealed);
  const fireCount = division ? fireZoneCount(fireZones) : 0;
  const { resetAll, busy } = useUnitMoves();
  const sideUnits = (side: "AMI" | "ENI" | "NEUTRE") => deployment?.units.filter((u) => u.side === side) ?? [];
  const unitCount = (side: "AMI" | "ENI" | "NEUTRE") =>
    deployment?.units.filter((u) => u.side === side && u.positions[phase] && !u.hidden).length ?? 0;

  const { data: detail } = useQuery({
    queryKey: ["map", mapSlug],
    queryFn: () => api.map(mapSlug!),
    enabled: Boolean(mapSlug),
  });
  const counts = detail?.layer_counts;
  // Zone libre hors de France : fond Mapbox imposé (préférence gardée), fonds IGN grisés (docs/14 §2.7.10).
  const { zone } = useOpenZone();
  const forced = forcedBasemap({ engine: config.mapEngine, kind: zone ? "user" : detail?.kind, bbox: zone?.bbox ?? detail?.bbox });
  const effectiveBasemap = basemapShown(config.mapEngine, basemap, forced);

  return (
    <FloatingPanel
      enter="left"
      width="layers"
      icon={Layers}
      title={t("Calques")}
      open={layersOpen}
      onToggle={toggleLayers}
      actions={<HelpButton screen="carte" topic="carte.calques" buttonSize="sm" size={16} />}
    >
      <LayerGroup
        label={t("Ordre de bataille (ODB)")}
        total={deployment ? unitCount("AMI") + unitCount("ENI") + unitCount("NEUTRE") : 0}
        actions={<OpenObjects filter="all" label={t("Voir l'ordre de bataille")} icon={<Network size={14} />} />}
      >
        <SideRow
          label={t("Amis")}
          icon={<Users size={14} />}
          units={sideUnits("AMI")}
          unplaced={deployment?.unplaced ?? []}
          phase={phase}
          checked={unitsVisible.AMI}
          onChange={(v) => setUnitsVisible("AMI", v)}
          disabled={!deployment}
        />
        <SideRow
          label={t("Ennemis")}
          icon={<Swords size={14} />}
          units={sideUnits("ENI")}
          phase={phase}
          checked={unitsVisible.ENI}
          onChange={(v) => setUnitsVisible("ENI", v)}
          disabled={!deployment}
          attention={ENEMIES_ROW_TARGET}
        />
        {/* Carte tactique : la mêlée seule par défaut ; appuis (artillerie, génie, sol-air…) et soutien sur demande. */}
        <LayerRow
          label={t("Appuis et soutien")}
          icon={<Layers2 size={14} />}
          count={supportCount}
          checked={supportsVisible}
          onChange={setSupportsVisible}
          disabled={!deployment || supportCount === 0}
          title={t("Unités d'appui et de soutien : cachées par défaut, la carte tactique ne montre que la mêlée")}
        />
        {/* Rôle tactique lu en profondeur (docs/25 §4) : repère discret sous chaque symbole. */}
        <LayerRow
          label={t("Rôles tactiques")}
          icon={<Tags size={14} />}
          checked={roleLabelsVisible}
          onChange={setRoleLabelsVisible}
          disabled={!deployment || !deployment.posture}
          title={t("Repère du rôle de chaque unité sous son symbole : 1er échelon, 2e échelon, réserve, reconnaissance, appui, soutien")}
        />
        <Presence show={unitCount("NEUTRE") > 0}>
          <Motion origin="fade">
            <SideRow
              label={t("Populations")}
              icon={<UserRound size={14} />}
              units={sideUnits("NEUTRE")}
              phase={phase}
              checked={unitsVisible.NEUTRE}
              onChange={(v) => setUnitsVisible("NEUTRE", v)}
            />
          </Motion>
        </Presence>
        <Presence
          show={Boolean(deployment && (deployment.moves.count + deployment.moves.lines > 0 || deployment.placement?.measures))}
        >
          <Motion origin="fade" className="px-2 py-2.5">
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => resetAll.mutate()}
              title={t("Remet les unités et les lignes déplacées à leur place doctrinale")}
            >
              <RotateCcw size={14} /> {t("Rétablir le dispositif doctrinal")}
            </Button>
          </Motion>
        </Presence>
        {!deployment && (
          <p className="px-2 py-1 text-meta text-ink-muted">
            {revealed
              ? t("Choisissez un camp et une mission pour déployer les unités.")
              : t("Les unités apparaissent à la création du dossier.")}
          </p>
        )}
      </LayerGroup>
      <LayerGroup
        label={t("Mesures de coordination")}
        total={(corridor ? 2 + corridor.lines.length : 0) + measureCount + graphicsCount(graphicsOf(deployment)) + roadCount + fireCount}
      >
        <LayerRow
          label={
            !corridor
              ? t("Fuseau")
              : corridor.width_m === null
                ? t("Fuseau (limites du camp)")
                : t("Fuseau ({km} km)", { km: (corridor.width_m / 1000).toLocaleString(locale()) })
          }
          icon={<SplitSquareVertical size={14} />}
          count={corridor ? 2 : 0}
          checked={controlVisible.fuseau}
          onChange={(v) => setControlVisible("fuseau", v)}
          disabled={!corridor}
        />
        <LayerRow
          label={t("Lignes {first} à {last}", { first: lineLabel("L0"), last: lineLabel("L3") })}
          icon={<Milestone size={14} />}
          count={corridor?.lines.length ?? 0}
          checked={controlVisible.lima}
          onChange={(v) => setControlVisible("lima", v)}
          disabled={!corridor}
        />
        {/* Deux cases indépendantes, sans désactivation (R12) : cocher les noms rétablit aussi les lignes. Même état
            que le bouton de la barre basse et la touche N, mémorisé par poste. */}
        <Presence show={measures !== null}>
          <Motion origin="fade">
            <LayerRow
              label={t("Lignes et zones de la ZO")}
              icon={<Spline size={14} />}
              count={measureCount}
              checked={controlVisible.measures}
              onChange={(v) => setControlVisible("measures", v)}
            />
            <LayerRow
              depth={2}
              label={t("Noms des lignes et des zones")}
              icon={<Tags size={14} />}
              checked={namesShown(controlVisible)}
              onChange={setNamesShown}
            />
          </Motion>
        </Presence>
        <LayerRow
          label={t("Graphiques tactiques")}
          icon={<Shapes size={14} />}
          count={graphicsCount(graphicsOf(deployment))}
          checked={controlVisible.graphics !== false}
          onChange={(v) => setControlVisible("graphics", v)}
          disabled={!deployment}
          trailing={<OpenObjects size="xs" filter="graphics" label={t("Dessiner un graphique tactique")} icon={<PencilRuler size={14} />} />}
        />
        <LayerRow
          label={t("Réseau de manœuvre")}
          icon={<Route size={14} />}
          count={roadCount}
          checked={controlVisible.roads === true}
          onChange={(v) => setControlVisible("roads", v)}
          disabled={!corridor || !exerciseId}
        />
        {/* Légende du réseau affiché : couleurs de l'iPad, comme la carte C7 de l'annexe C. */}
        <Presence show={controlVisible.roads === true && roadCount > 0}>
          <Motion origin="fade">
            <div className="flex items-center gap-3 px-2 pb-1 text-meta text-ink-muted">
              {(
                [
                  [PENETRANTE_RED, t("Pénétrante (P)")],
                  [ROCADE_GREEN, t("Rocade (R)")],
                ] as const
              ).map(([colour, name]) => (
                <span key={name} className="flex items-center gap-1.5">
                  <span aria-hidden className="h-[3px] w-4 rounded-full" style={{ background: colour }} />
                  {name}
                </span>
              ))}
            </div>
          </Motion>
        </Presence>
        {/* ZEF et ZEA de l'annexe A (OPO de division) : mêmes zones que la page A4, masquables. */}
        <Presence show={division}>
          <Motion origin="fade">
            <LayerRow
              label={t("ZEF et ZEA (annexe A)")}
              icon={<Crosshair size={14} />}
              count={fireCount}
              checked={controlVisible.fireZones !== false}
              onChange={(v) => setControlVisible("fireZones", v)}
              title={fireCount ? undefined : t("Les zones apparaissent une fois l'OPO de division rédigé.")}
            />
          </Motion>
        </Presence>
        {corridor && corridor.missing.length > 0 && (
          <p className="px-2 py-1 text-meta leading-snug text-ink-muted">
            {isCompany(deployment?.level)
              ? // Zone d'un S/GTIA : les LIMA sont celles du GTIA, sans en créer de nouvelles (docs/21 §3).
                t("{lines} absentes du fuseau du GTIA : la zone du S/GTIA n'en crée pas et son ordre ne les cite pas.", {
                  lines: corridor.missing.join(", "),
                })
              : t("{lines} sans objet : le dispositif ami ne bouge pas entre deux temps.", { lines: corridor.missing.join(", ") })}
          </p>
        )}
      </LayerGroup>
      {/* Groupe « Drones » (chantier « Drones », exception accordée à la carte figée) : décoché par défaut, masqué en
          CENTAC (rien de nouveau dans la rotation). */}
      <DronesGroup
        level={deployment?.level ?? null}
        centac={Boolean(deployment?.centac) || deployment?.level === "CENTAC"}
        enemies={unitCount("ENI")}
      />
      {/* Groupe « Météo » : calque « Visibilité » (conception validée le 31/10/2026), CENTAC compris (météo de saison). */}
      <LayerGroup label={t("Météo")}>
        <VisibilityRow deployed={Boolean(deployment)} />
      </LayerGroup>
      {/* Annotations APP-6 (exception à la carte figée du 15/10/2026) : un calque par catégorie, visible par défaut. */}
      <LayerGroup
        label={t("Annotations")}
        total={annotations.length}
        actions={<OpenObjects filter="annotations" label={t("Ajouter une annotation")} icon={<PencilRuler size={14} />} />}
      >
        {CATEGORIES.map((category) => {
          const key = annotationControl(category.key);
          const sample = symbolsOf(category.key)[0]!;
          return (
            <LayerRow
              key={category.key}
              label={labelled(category)}
              icon={<SymbolThumb square sidc={sidcOf(sample.key)} />}
              count={annotations.filter((a) => symbolOf(a.key)?.category === category.key).length}
              checked={controlVisible[key] !== false}
              onChange={(v) => setControlVisible(key, v)}
              disabled={!deployment}
            />
          );
        })}
      </LayerGroup>
      {GROUPS.map((group) => (
        <LayerGroup
          key={group.label}
          label={t(group.label)}
          total={
            counts
              ? group.items.reduce((sum, it) => sum + (it.key === "boundary" ? 0 : (counts[it.key] ?? 0)), 0)
              : undefined
          }
        >
          {group.items.map((it) => (
            <LayerRow
              key={it.key}
              label={t(it.label)}
              icon={it.icon}
              count={it.key === "boundary" ? undefined : counts?.[it.key]}
              checked={it.key === "rivers" && zone ? freeTerrainRivers : visible[it.key]}
              onChange={(v) => (it.key === "rivers" && zone ? setFreeTerrainRivers(v) : setVisible(it.key, v))}
              disabled={!mapSlug}
            />
          ))}
          {group.borders && <CountryBordersRow />}
        </LayerGroup>
      ))}
      <LayerGroup label={t("Fond de carte")}>
        <div className="space-y-1.5 px-2 pt-1 pb-1">
          <Segmented<Basemap>
            size="sm"
            ariaLabel={t("Fond de carte")}
            value={effectiveBasemap}
            onChange={setBasemap}
            options={BASEMAPS.map((b) => {
              const outside = forced !== null && b.key !== forced;
              return {
                value: b.key,
                label: t(b.label),
                disabled: !b.available || outside,
                title: outside ? t("Fond disponible en France seulement") : b.available ? undefined : t("Jeton Mapbox non configuré"),
              };
            })}
          />
          <Presence show={forced !== null}>
            <Motion origin="fade">
              <p className="px-1 text-meta leading-snug text-ink-muted">{t("Fond disponible en France seulement : le terrain est affiché sur la carte Mapbox.")}</p>
            </Motion>
          </Presence>
          <label
            className="flex h-7 items-center gap-2 rounded-md pr-1 text-ui"
            title={t("Éclaircit le fond de carte ; les couches du camp, les unités et les lignes restent nettes")}
          >
            <Sun size={14} className="shrink-0 text-ink-muted" />
            <span className="shrink-0 text-ink">{t("Éclaircir")}</span>
            <input
              type="range"
              min={0}
              max={80}
              step={5}
              value={Math.round(mapLighten * 100)}
              onChange={(e) => setMapLighten(Number(e.target.value) / 100)}
              aria-label={t("Éclaircir le fond de carte")}
              className="h-1 min-w-0 flex-1 cursor-pointer accent-accent"
            />
            <span className="w-9 shrink-0 text-right text-ink-muted tabular-nums">{t("{n} %", { n: Math.round(mapLighten * 100) })}</span>
          </label>
        </div>
      </LayerGroup>
    </FloatingPanel>
  );
}
